/* eslint-disable max-params */
import type { GraphFileItem, GraphListResponse } from "../models"

import { logger } from "@/lib/logger"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * MUTATION: Removes a file from a session
 * @param sessionId - The session ID
 * @param fileName - The name of the file to remove
 * @param idToken - Optional authentication token
 * @category Mutation
 */
export async function removeFileApi(
  sessionId: string,
  fileName: string,
  idToken?: string
): Promise<Response> {
  return fetchWithAuthRetry(
    `${BACKEND_URI}/remove-file/${sessionId}/${fileName}`,
    {
      method: "DELETE",
      headers: getHeaders(idToken),
    },
    idToken
  )
}

/**
 * MUTATION: Removes a session file reference (for Responses API uploaded files)
 * This removes the file from the session's file_refs in Cosmos DB,
 * NOT from Azure AI Search index.
 * @param sessionId - The session ID
 * @param fileName - The name of the file to remove
 * @param idToken - Optional authentication token
 * @category Mutation
 */
export async function removeSessionFileApi(
  sessionId: string,
  fileName: string,
  idToken?: string
): Promise<Response> {
  return fetchWithAuthRetry(
    `${BACKEND_URI}/remove-session-file/${sessionId}/${encodeURIComponent(fileName)}`,
    {
      method: "DELETE",
      headers: getHeaders(idToken),
    },
    idToken
  )
}

/**
 * MUTATION: Uploads files to a session
 * @param sessionId - The session ID
 * @param files - Array of files to upload
 * @param idToken - Optional authentication token
 * @category Mutation
 */
export async function uploadFileApi(
  sessionId: string,
  files: File[],
  idToken?: string
): Promise<Response> {
  logger.debug("Starting upload of files", { fileCount: files.length })
  logger.debug("For session", { sessionId })
  logger.debug("Files", { files })

  const formData = new FormData()

  files.forEach((file) => {
    formData.append("files", file)
    formData.append("session_id", sessionId)
  })

  return fetchWithAuthRetry(
    `${BACKEND_URI}/upload-files`,
    {
      method: "POST",
      headers: getHeaders(idToken, true),
      body: formData,
    },
    idToken
  )
}

/**
 * MUTATION: Uploads a Microsoft Graph file to a session
 * @param sessionId - The session ID
 * @param item - Graph file item to upload
 * @param idToken - Optional authentication token
 * @category Mutation
 */
export async function uploadGraphFileApi(
  sessionId: string,
  item: GraphFileItem,
  idToken?: string
): Promise<Response> {
  return fetchWithAuthRetry(
    `${BACKEND_URI}/graph/upload`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        session_id: sessionId,
        drive_id: item.driveId,
        item_id: item.id,
        name: item.name,
        mime_type: item.mimeType,
      }),
    },
    idToken
  )
}

/**
 * MUTATION: Uploads an image for Canvas editor
 * @param sessionId - The session ID
 * @param file - Image file to upload
 * @param idToken - Optional authentication token
 * @returns Uploaded image metadata
 * @category Mutation
 */
export async function uploadCanvasImageApi(
  sessionId: string,
  file: File,
  idToken?: string
): Promise<{ url: string; filename: string; size: number }> {
  logger.debug("Uploading Canvas image", { filename: file.name, size: file.size })

  const formData = new FormData()
  formData.append("file", file)
  formData.append("session_id", sessionId)

  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/canvas/upload-image`,
    {
      method: "POST",
      headers: getHeaders(idToken, true),
      body: formData,
    },
    idToken
  )

  if (!response.ok) {
    throw new Error(`Failed to upload image: ${response.status} ${response.statusText}`)
  }

  return await response.json()
}

/**
 * Error thrown by `downloadGraphFileApi` when the backend rejects the
 * download with a JSON error body (e.g. an unreadable/encrypted file —
 * see `graph_download_file`'s OLE2/CFBF check). Carries the backend's
 * `translation_key`/`translation_params` when present so callers can show a
 * localized message instead of the raw (English) backend text.
 */
export class GraphDownloadError extends Error {
  translationKey?: string
  translationParams?: Record<string, unknown>

  constructor(
    message: string,
    translationKey?: string,
    translationParams?: Record<string, unknown>
  ) {
    super(message)
    this.name = "GraphDownloadError"
    this.translationKey = translationKey
    this.translationParams = translationParams
  }
}

/**
 * QUERY: Downloads a Microsoft Graph file as a Blob
 * @param item - Graph file item to download
 * @param idToken - Optional authentication token
 * @returns File blob
 * @category Query
 */
export async function downloadGraphFileApi(item: GraphFileItem, idToken?: string): Promise<Blob> {
  const url = new URL(`${BACKEND_URI}/graph/download`)
  url.searchParams.set("drive_id", item.driveId)
  url.searchParams.set("item_id", item.id)
  url.searchParams.set("name", item.name)
  if (item.mimeType) url.searchParams.set("mime_type", item.mimeType)

  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  if (!response.ok) {
    // The backend returns a JSON body (not the file) for known failures —
    // e.g. an unreadable/encrypted file rejected before it would have
    // streamed ciphertext back with no explanation. Best-effort parse: fall
    // back to the generic message for non-JSON bodies (network errors,
    // proxy failures, etc.) so this never masks an unrelated problem.
    try {
      const body = (await response.clone().json()) as {
        error?: string
        translation_key?: string
        translation_params?: Record<string, unknown>
      }
      if (body.error) {
        throw new GraphDownloadError(body.error, body.translation_key, body.translation_params)
      }
    } catch (parseError) {
      if (parseError instanceof GraphDownloadError) throw parseError
      // Not a JSON body — fall through to the generic error below.
    }
    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`)
  }
  return await response.blob()
}

/**
 * QUERY: Lists files from Microsoft Graph (OneDrive, Shared, Recent)
 * @param source - File source type (oneDrive, sharedWithMe, recent)
 * @param cursor - Pagination cursor
 * @param pageSize - Number of items per page
 * @param idToken - Optional authentication token
 * @returns Graph file list response
 * @category Query
 */
export async function listGraphFilesApi(
  source: string,
  cursor?: string,
  pageSize?: number,
  idToken?: string
): Promise<GraphListResponse> {
  const url = new URL(`${BACKEND_URI}/graph/list-files`)
  url.searchParams.set("source", source)
  if (pageSize) url.searchParams.set("page_size", pageSize.toString())
  if (cursor) url.searchParams.set("cursor", cursor)

  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  return response.json()
}

/**
 * QUERY: Lists children of a Microsoft Graph drive item (folder contents)
 * @param driveId - Drive ID
 * @param itemId - Parent item ID (use "root" for drive root)
 * @param cursor - Pagination cursor
 * @param pageSize - Number of items per page
 * @param idToken - Optional authentication token
 * @returns Graph file list response
 * @category Query
 */
export async function listGraphDriveChildrenApi(
  driveId: string,
  itemId: string,
  cursor?: string,
  pageSize?: number,
  idToken?: string
): Promise<GraphListResponse> {
  const url = new URL(`${BACKEND_URI}/graph/drive-children`)
  url.searchParams.set("drive_id", driveId)
  url.searchParams.set("item_id", itemId)
  if (pageSize) url.searchParams.set("page_size", pageSize.toString())
  if (cursor) url.searchParams.set("cursor", cursor)

  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  return response.json()
}

/**
 * QUERY: Lists SharePoint sites
 * @param mode - Site listing mode (auto, followed, discovered)
 * @param cursor - Pagination cursor
 * @param pageSize - Number of items per page
 * @param idToken - Optional authentication token
 * @returns SharePoint sites list
 * @category Query
 */
export async function listGraphSharePointSitesApi(
  mode: "auto" | "followed" | "discovered" = "auto",
  cursor?: string,
  pageSize?: number,
  idToken?: string
): Promise<{
  items: Array<{
    siteId: string
    siteUrl: string
    displayName: string
    lastModified: string
    drivesCount: number
  }>
  next_cursor?: string | null
}> {
  const url = new URL(`${BACKEND_URI}/graph/sharepoint-sites`)
  if (pageSize) url.searchParams.set("page_size", pageSize.toString())
  if (cursor) url.searchParams.set("cursor", cursor)
  url.searchParams.set("mode", mode)
  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  return response.json()
}

/**
 * QUERY: Lists SharePoint libraries for a specific site
 * @param siteId - SharePoint site ID
 * @param cursor - Pagination cursor
 * @param pageSize - Number of items per page
 * @param idToken - Optional authentication token
 * @returns SharePoint libraries list
 * @category Query
 */
export async function listGraphSharePointLibrariesApi(
  siteId: string,
  cursor?: string,
  pageSize?: number,
  idToken?: string
): Promise<{
  items: Array<{ driveId: string; name: string; lastModified: string }>
  next_cursor?: string | null
}> {
  const url = new URL(`${BACKEND_URI}/graph/sharepoint-libraries`)
  url.searchParams.set("site_id", siteId)
  if (pageSize) url.searchParams.set("page_size", pageSize.toString())
  if (cursor) url.searchParams.set("cursor", cursor)
  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  return response.json()
}

/**
 * QUERY: Lists files from a SharePoint site
 * @param siteId - SharePoint site ID
 * @param cursor - Pagination cursor
 * @param pageSize - Number of items per page
 * @param idToken - Optional authentication token
 * @returns Graph file list response
 * @category Query
 */
export async function listGraphSiteFilesApi(
  siteId: string,
  cursor?: string,
  pageSize?: number,
  idToken?: string
): Promise<GraphListResponse> {
  const url = new URL(`${BACKEND_URI}/graph/site-files`)
  url.searchParams.set("site_id", siteId)
  if (pageSize) url.searchParams.set("page_size", pageSize.toString())
  if (cursor) url.searchParams.set("cursor", cursor)
  const response = await fetchWithAuthRetry(
    url.toString(),
    { method: "GET", headers: getHeaders(idToken) },
    idToken
  )
  return response.json()
}

/**
 * QUERY: Lists files for a session
 * @param sessionId - The session ID
 * @param idToken - Optional authentication token
 * @returns Array of file information
 * @category Query
 */
export async function listFileApi(
  sessionId: string,
  idToken?: string,
  projectId?: string
): Promise<string[]> {
  const url = new URL(`${BACKEND_URI}/list-files`)
  url.searchParams.set("session_id", sessionId)
  if (projectId) {
    url.searchParams.set("project_id", projectId)
  }

  const response = await fetchWithAuthRetry(
    url.toString(),
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return response.json()
}
